import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Database } from 'lucide-react';
import { registerRequestSchema } from '@pglearn/shared';
import { Button } from '../components/ui/Button.js';
import { Field } from '../components/ui/Field.js';
import { Alert, Card } from '../components/ui/Card.js';
import { useAuth } from '../lib/auth.js';
import { ApiRequestError } from '../lib/api.js';

export function Register() {
  const { register, registrationOpen } = useAuth();
  const navigate = useNavigate();

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);
    setFieldErrors({});

    const form = new FormData(event.currentTarget);
    const parsed = registerRequestSchema.safeParse({
      email: form.get('email'),
      password: form.get('password'),
      displayName: form.get('displayName'),
      // Detecting the zone here means streaks are counted against the
      // learner's own calendar day from their very first solve.
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });

    if (!parsed.success) {
      const errors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? 'form');
        errors[key] ??= issue.message;
      }
      setFieldErrors(errors);
      return;
    }

    setIsSubmitting(true);
    try {
      await register(parsed.data);
      void navigate('/dashboard', { replace: true });
    } catch (error) {
      setFormError(
        error instanceof ApiRequestError
          ? error.message
          : 'Could not create your account. Please try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 py-12 dark:bg-slate-950">
      <div className="w-full max-w-md">
        <Link to="/" className="mb-8 flex items-center justify-center gap-2">
          <Database className="size-6 text-brand-600 dark:text-brand-400" aria-hidden="true" />
          <span className="font-semibold text-slate-900 dark:text-slate-100">pgLearn</span>
        </Link>

        <Card className="p-8">
          {registrationOpen === false ? (
            <>
              <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                Sign-ups are paused
              </h1>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                New accounts are not being created on pgLearn at the moment. This is
                temporary — if you already have an account you can still sign in as
                usual.
              </p>
              <Button
                type="button"
                className="mt-6 w-full"
                onClick={() => void navigate('/login')}
              >
                Go to sign in
              </Button>
            </>
          ) : (
            <>
              <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                Create your account
              </h1>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Start at Level 1 and write your first query in a minute.
              </p>

              <form onSubmit={handleSubmit} className="mt-6 space-y-4" noValidate>
                {formError && <Alert>{formError}</Alert>}

                <Field
                  label="Name"
                  name="displayName"
                  autoComplete="name"
                  required
                  error={fieldErrors.displayName}
                />
                <Field
                  label="Email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  error={fieldErrors.email}
                />
                <Field
                  label="Password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  hint="At least 10 characters. Length matters more than symbols."
                  error={fieldErrors.password}
                />

                <Button type="submit" className="w-full" isLoading={isSubmitting}>
                  Create account
                </Button>
              </form>

              <p className="mt-6 text-center text-sm text-slate-600 dark:text-slate-400">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="font-medium text-brand-600 hover:underline dark:text-brand-400"
                >
                  Sign in
                </Link>
              </p>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
